import {useMemo} from "react";
import {Line} from "recharts";
import {BaseChart, ChartComponent} from "./BaseChart";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {FieldResult} from "../../../types/domain/fieldResult";
import {useMeasureSystemUtils} from "../../../hooks/measureSystemUtilsHooks";
import {MeasureSystemUtils} from "../../../types/domain/measureSystemUtils";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";

const formatOneDecimal = (value: number) => value.toFixed(1);

export const DryMatterProductionCumulativeChart: ChartComponent = ({data}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const chartData = useMemo(() => data.map((d) => mapFieldResultsToChartData(d, msUtils)), [data, msUtils]);
    const unit = msUtils.getProductionUnits();

    return (
        <BaseChart
            title={t("insights.charts.dry_matter_prod_cumulative.title")}
            yLabel={`${t("insights.charts.dry_matter_prod_cumulative.y_label")}, ${unit}`}
            data={chartData}
            helpPopoverTranslationKey="help.popover.fields.dry_matter_production"
            tooltipValueFormatter={formatOneDecimal}
        >
            <Line
                type="monotone"
                dataKey="cumulativeAttainableCropGrowth"
                name={t("insights.charts.dry_matter_prod_cumulative.line2_name")}
                unit={unit}
                yAxisId="left"
                stroke="#00CA4D"
                fill="#00CA4D"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="cumulativeUnlimitedWaterCropGrowth"
                name={t("insights.charts.dry_matter_prod_cumulative.line3_name")}
                unit={unit}
                yAxisId="left"
                stroke="#F8A25A"
                fill="#F8A25A"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="cumulativeCropGrowth"
                name={t("insights.charts.dry_matter_prod_cumulative.line1_name")}
                unit={unit}
                yAxisId="left"
                stroke="#5161C1"
                fill="#5161C1"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: FieldResult, msUtils: MeasureSystemUtils): DataProps => {
    return {
        date: dayjs(r.date).toDate(),
        cumulativeCropGrowth: msUtils.getProduction(r.aggregatedProps.crop_production_cumulative),
        cumulativeAttainableCropGrowth: msUtils.getProduction(
            r.aggregatedProps.attainable_biomass_production_cumulative,
        ),
        cumulativeUnlimitedWaterCropGrowth: msUtils.getProduction(
            r.aggregatedProps.water_unlimited_crop_production_cumulative,
        ),
    };
};

export interface DataProps extends TimeSeriesDataProps {
    cumulativeCropGrowth: number;
    cumulativeAttainableCropGrowth: number;
    cumulativeUnlimitedWaterCropGrowth: number;
}
